import { useContext } from "react";
import { RadioChangeEvent } from "antd";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import LoaderContext from "../../../Context/LoaderContext/Index";
import { useInterviewRoundContext } from "../../../Context/InterviewRoundContext";
import aiInterviewService from "../../../Services/ai-interview.service";
import { openNotification } from "../../../Components/Notification/Index";

interface InterviewCommonHandlersReturnType {
  handleSubmitStep: () => void;
  toggleToFullscreenMode: () => void;
  toggleToNormalScreenMode: () => void;
  handleCancelExit: () => void;
  handleConfirmExit: () => void;
  handleAnswerSingleQuestion: (index: number) => Promise<void>;
  handleBack: () => void;
  handleNext: () => Promise<void>;
  handleQuestionAnswerChange: (
    e: React.ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent,
    value?: string | null
  ) => void;
  onNavigateToQuestions: () => void;
}

const useInterviewCommonHandlers = (): InterviewCommonHandlersReturnType => {
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const {
    setActiveQuestionIndex,
    currentSessionQuestions,
    isRoundFinished,
    currentSession,
    isFullscreen,
    elementRef,
    activeQuestionIndex,
    setShowExitConfirmation,
    setExitTimer,
    setCurrentSessionQuestionAnswers,
    currentSessionQuestionAnswers,
    setCurrentSessionQuestions,
  } = useInterviewRoundContext();

  const getBrowserFingerPrint = async () => {
    const { hash } = await getFingerprint(true);
    return hash;
  };

  const handleSubmitStep = () => {
    setActiveQuestionIndex(currentSessionQuestions.length);
  };

  const toggleToFullscreenMode = () => {
    if (!isFullscreen) {
      if (elementRef.current) {
        if (elementRef.current.requestFullscreen) {
          elementRef.current.requestFullscreen();
        } else if (elementRef.current.mozRequestFullScreen) {
          elementRef.current.mozRequestFullScreen(); // Firefox
        } else if (elementRef.current.webkitRequestFullscreen) {
          elementRef.current.webkitRequestFullscreen(); // Chrome, Safari, Opera
        } else if (elementRef.current.msRequestFullscreen) {
          elementRef.current.msRequestFullscreen(); // IE/Edge
        } else {
          console.error("Fullscreen API is not supported on this element.");
        }
      } else {
        console.error("Element reference is null.");
      }
    }
  };

  const toggleToNormalScreenMode = () => {
    if (isFullscreen && document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setExitTimer(false);
    const isFullScreenActive =
      document.fullscreenElement !== elementRef?.current;

    if (elementRef?.current && isFullScreenActive) {
      elementRef.current.requestFullscreen().catch((err: any) => {
        console.error("Failed to re-enter fullscreen:", err);
      });
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    setExitTimer(false);
  };

  const handleAnswerSingleQuestion = async (index: number) => {
    // If clicking on the same question, do nothing
    if (index === activeQuestionIndex) {
      return;
    }

    // Prevent going back to instructions once round has started or completed
    if (activeQuestionIndex > -1 && index === -1) {
      return;
    }

    // If clicking on a previous question, just navigate
    if (index < activeQuestionIndex) {
      setActiveQuestionIndex(index);
      return;
    }

    const currentQuestion = currentSessionQuestions[activeQuestionIndex];

    if (
      !currentQuestion &&
      activeQuestionIndex !== currentSessionQuestions.length
    ) {
      console.log("No question found", activeQuestionIndex);
      return;
    }

    if (!currentSession) {
      console.error("No session found");
      return;
    }

    // If round is finished, allow navigation to any question
    if (isRoundFinished) {
      setActiveQuestionIndex(index);
      return;
    }

    // If current question is already answered, allow navigation
    const isAnswered =
      currentQuestion?.responses && currentQuestion.responses.length > 0;
    if (isAnswered) {
      setActiveQuestionIndex(index);
      return;
    }

    // Check if current question has an answer
    const questionType = currentQuestion?.type;
    const answer =
      questionType === "fileUpload"
        ? currentSessionQuestionAnswers[activeQuestionIndex]?.fileUrl
        : currentSessionQuestionAnswers[activeQuestionIndex]?.answer;

    // If there's no answer, just navigate to the next question
    if (!answer) {
      setActiveQuestionIndex(index);
      return;
    }

    // If there is an answer, submit it before navigating
    setLoading(true);
    setLoadingMessage("Submitting answer please wait...");

    try {
      const browserFingerprint = await getBrowserFingerPrint();
      const updatedQuestion = await aiInterviewService.answerSingleQuestion(
        answer,
        currentSession._id,
        currentQuestion._id,
        browserFingerprint
      );

      if (updatedQuestion) {
        setActiveQuestionIndex(index);
        setCurrentSessionQuestions((prev) => {
          const newQuestions = [...prev];
          newQuestions[activeQuestionIndex] = updatedQuestion;
          return newQuestions;
        });

        setCurrentSessionQuestionAnswers((prev) => {
          const newAnswers = [...prev];
          newAnswers[activeQuestionIndex] = {
            questionId: updatedQuestion._id,
            answer: questionType !== "fileUpload" ? answer : null,
            fileUrl: questionType === "fileUpload" ? answer : null,
          };
          return newAnswers;
        });
      } else {
        console.error("Failed to answer question");
        openNotification(
          "error",
          "Failed to answer question",
          "Please try again"
        );
        setActiveQuestionIndex(index); // Still navigate even if submission fails
      }
    } catch (error) {
      console.error("Error handling next question", error);
      setActiveQuestionIndex(index); // Still navigate even if there's an error
    } finally {
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage(null);
      }, 1000);
    }
  };

  const handleBack = () => {
    // Prevent going back to instructions once round has started or completed
    if (activeQuestionIndex > -1) {
      if (activeQuestionIndex > 0) {
        setActiveQuestionIndex(activeQuestionIndex - 1);
      }
    }
  };

  const handleNext = async () => {
    const nextIndex = activeQuestionIndex + 1;
    if (nextIndex <= currentSessionQuestions.length) {
      await handleAnswerSingleQuestion(nextIndex);
    }
  };

  const handleQuestionAnswerChange = (
    e: React.ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent | null,
    value?: string | null
  ) => {
    if (isRoundFinished) {
      console.warn("handleQuestionAnswer: Round is already finished");
      return;
    }

    const currentQuestion = currentSessionQuestions[activeQuestionIndex];
    if (!currentQuestion) {
      console.error("handleQuestionAnswer: No question found");
      return;
    }
    const { type: currentQuestionType } = currentQuestion;

    setCurrentSessionQuestionAnswers((prev) => {
      const newAnswers = [...prev];
      const prevAnswer = newAnswers[activeQuestionIndex];

      if (currentQuestionType === "coding") {
        newAnswers[activeQuestionIndex] = {
          ...prevAnswer,
          answer: value ?? null,
        };
      } else if (e?.target) {
        const answer = e.target.value;
        if (currentQuestionType === "fileUpload") {
          newAnswers[activeQuestionIndex] = {
            ...prevAnswer,
            fileUrl: answer,
          };
        } else {
          const isSameAnswer = answer === prevAnswer?.answer;
          newAnswers[activeQuestionIndex] = {
            ...prevAnswer,
            answer: isSameAnswer ? null : answer,
          };
        }
      }
      return newAnswers;
    });
  };

  const onNavigateToQuestions = () => {
    setActiveQuestionIndex(currentSessionQuestions.length - 1);
  };

  return {
    handleSubmitStep,
    toggleToFullscreenMode,
    toggleToNormalScreenMode,
    handleCancelExit,
    handleConfirmExit,
    handleAnswerSingleQuestion,
    handleBack,
    handleNext,
    handleQuestionAnswerChange,
    onNavigateToQuestions,
  };
};

export default useInterviewCommonHandlers;